import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
  useRef,
} from "react";
import {
  Container,
  Grid,
  FormLabel,
  TextField,
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useLocation } from "react-router-dom";
import dataService from "../../services/dataService";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MarketingServicesHeaderImage from "../../../img/lansing_marketing_services.jpg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { validateWebsite } from "@/app/utils/validatorHelper";
export type MarketingCommentType =
  | "None"
  | "LogoDesign"
  | "BusinessCards"
  | "DirectMail"
  | "Flyers"
  | "Clothing"
  | "Brochures"
  | "Jobsigns"
  | "HomeshowItems"
  | "WebsiteSocialMedia"
  | "Other";

export interface MarketingContactUsDto {
  companyName: string;
  phoneNumber: string;
  email: string;
  branchName: string;
  branchId?: number;
  branchUrl?: string;
  comment?: string;
  captchaV3PublicKey?: string;
  branches: string[];
  marketingCommentType: MarketingCommentType[];
  contactName: string;
  websiteUrl?: string;
  logo?: File;
  file?: File;
}

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, opts: { action: string }): Promise<string>;
    };
  }
}

// ---- HELPERS ----
const onlyDigits = (s: string) => s.replace(/\D/g, "");
const formatDashedPhone = (digits: string) => {
  const d = digits.slice(0, 10);
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 10);
  if (d.length <= 3) return a;
  if (d.length <= 6) return `${a}-${b}`;
  return `${a}-${b}-${c}`;
};
const isDashedPhoneValid = (val: string) => /^\d{3}-\d{3}-\d{4}$/.test(val);

const baseText = {
  fontSize: "14px",
  fontFamily: `"Montserrat", Verdana, sans-serif`,
} as const;

const fieldRowSx = {
  position: "relative" as const,
  width: "80%",
  mx: "auto",
  overflow: "visible",
};

const errorTextSx = {
  ...baseText,
  color: "#af272f",
  mt: 0.5,
} as const;

// ---- BorderedInput ----
type BorderedInputProps = {
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  type?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
};
const BorderedInput = memo(function BorderedInput({
  name,
  value,
  onChange,
  required,
  type = "text",
  inputProps,
  onKeyDown,
  onPaste,
}: BorderedInputProps) {
  return (
    <Box
      sx={{
        borderWidth: 1,
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        px: "10px",
        borderRadius: "4px",
      }}
    >
      <TextField
        variant="standard"
        fullWidth
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        InputProps={{
          disableUnderline: true,
          sx: {
            ...baseText,
            color: "#000",
            height: "32px",
            "& input": {
              fontSize: "14px",
            },
            inputProps: inputProps,
          },
        }}
      />
    </Box>
  );
});

// ---- Hook for query ----
const useQuery = () => new URLSearchParams(useLocation().search);

const MARKETING_OPTIONS: { value: MarketingCommentType; label: string }[] = [
  { value: "LogoDesign", label: "Logo Design" },
  { value: "BusinessCards", label: "Business Cards" },
  { value: "DirectMail", label: "Direct Mail" },
  { value: "Flyers", label: "Flyers" },
  { value: "Clothing", label: "Clothing" },
  { value: "Brochures", label: "Brochures" },
  { value: "Jobsigns", label: "Job Signs" },
  { value: "HomeshowItems", label: "Home Show Items" },
  { value: "WebsiteSocialMedia", label: "Website / Social Media" },
  { value: "Other", label: "Other" },
];

// ---- Logo Upload Types ----
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/bmp"];

type LogoUploadProps = {
  onFileChange?: (file: File | null) => void;
};

const LogoUpload: React.FC<LogoUploadProps> = ({ onFileChange }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFile = (file: File | null) => {
    if (!file) {
      setPreviewUrl(null);
      onFileChange?.(null);
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert("Please upload JPEG, JPG, PNG, or BMP image.");
      onFileChange?.(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onFileChange?.(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFile(file);
  };

  const handleChooseFile = () => {
    inputRef.current?.click();
  };

  const handleRemoveFile = () => {
    handleFile(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0] || null;
    handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box>
      <input
        name="logo"
        id="logo"
        type="file"
        aria-label="logo"
        ref={inputRef}
        accept=".jpg,.jpeg,.png,.bmp"
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      <Box
        sx={{
          width: "100%",
          border: "1px solid #d9d9d9",

          textAlign: "center",
          cursor: "pointer",
          mb: 1,
        }}
        onClick={handleChooseFile}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {!previewUrl ? (
          <Box id="noLogoWrapper">
            <Box sx={{ mb: 1 }}>
              <CloudUploadIcon sx={{ fontSize: 32 }} />
            </Box>

            <Typography sx={{ mb: 1 }}>
              <strong>Drag and Drop logo here</strong>
            </Typography>
          </Box>
        ) : (
          <Box>
            <img
              id="uploadedLogo"
              src={previewUrl}
              alt="Uploaded logo"
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
        )}
      </Box>

      <Typography
        id="logoImageMessage"
        sx={{
          ...baseText,
          fontSize: "12px",
          mb: 1,
          textAlign: "center",
        }}
      >
        Upload your logo as a JPEG, JPG, PNG, or BMP image. Email
        helpnow@lansingbp.com if you need assistance.
      </Typography>

      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          sx={{ mr: 1 }}
          onClick={handleChooseFile}
        >
          Choose File
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="small"
          color="error"
          onClick={handleRemoveFile}
          disabled={!previewUrl}
        >
          Remove File
        </Button>
      </Box>
    </Box>
  );
};

// ==== PROPS INTERFACE ====
type MarketingContactUsProps = {
  isAuthenticated: boolean;
};

// THE PAGE COMPONENT
export default function MarketingContactUsPage({
  isAuthenticated,
}: MarketingContactUsProps) {
  const [siteKey, setSiteKey] = useState<string | undefined>(undefined);

  type MarketingForm = {
    companyName: string;
    phoneNumber: string;
    email: string;
    contactName: string;
    websiteUrl: string;
    branchName: string;
    branchUrl?: string;
    branchId?: number;
    marketingCommentType: MarketingCommentType[];
    comment: string;
  };

  const [form, setForm] = useState<MarketingForm>({
    companyName: "",
    phoneNumber: "",
    email: "",
    contactName: "",
    websiteUrl: "",
    branchName: "",
    branchUrl: undefined,
    branchId: undefined,
    marketingCommentType: [],
    comment: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof MarketingForm, string>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    sev: "success" | "error";
  }>({
    open: false,
    msg: "",
    sev: "success",
  });

  const [branches, setBranches] = useState<string[]>([]);

  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await dataService.getMarketingContactUs();
        const data = resp?.data as MarketingContactUsDto | undefined;
        if (mounted && data) {
          setForm((prev) => ({
            ...prev,
            companyName: data.companyName ?? "",
            phoneNumber: data.phoneNumber
              ? formatDashedPhone(onlyDigits(data.phoneNumber))
              : "",
            email: data.email ?? "",
            contactName: data.contactName ?? "",
            websiteUrl: data.websiteUrl ?? "",
            branchName: data.branchName ?? "",
            branchUrl: data.branchUrl ?? undefined,
            branchId: data.branchId ?? undefined,
            marketingCommentType: Array.isArray(data.marketingCommentType)
              ? (data.marketingCommentType.filter(
                  (x) => x && x !== "None"
                ) as MarketingCommentType[])
              : [],
            comment: data.comment ?? "",
          }));

          setBranches(data.branches ?? []);

          // RECAPTCHA KEY
          const k = data.captchaV3PublicKey;
          setSiteKey(k || undefined);
        }
      } catch (e: any) {
        console.error("getMarketingContactUs failed:", e);
        setToast({
          open: true,
          msg: e?.message || "Failed to load form.",
          sev: "error",
        });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const ensureRecaptchaReady = useCallback(async (key?: string) => {
    if (!key) throw new Error("reCAPTCHA site key is not configured.");
    if (typeof window === "undefined")
      throw new Error("reCAPTCHA unavailable on server.");

    if (!window.grecaptcha) {
      const id = `grecaptcha-v3-${key}`;
      if (!document.getElementById(id)) {
        const s = document.createElement("script");
        s.id = id;
        s.async = true;
        s.defer = true;
        s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
          key
        )}`;
        const loaded = new Promise<void>((resolve, reject) => {
          s.onload = () => resolve();
          s.onerror = () =>
            reject(new Error("Failed to load reCAPTCHA script."));
        });
        document.head.appendChild(s);
        await loaded;
      } else {
        await new Promise((r) => setTimeout(r, 0));
      }
    }

    if (window.grecaptcha) {
      await new Promise<void>((resolve) =>
        window.grecaptcha!.ready(() => resolve())
      );
    } else {
      throw new Error("reCAPTCHA failed to initialize.");
    }
  }, []);

  const handlePhoneKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedControl = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
      ];
      if (allowedControl.includes(e.key)) return;
      if (
        (e.metaKey || e.ctrlKey) &&
        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
      )
        return;
      if (!/^\d$/.test(e.key)) e.preventDefault();
    },
    []
  );

  const handlePhonePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      const digits = onlyDigits(pasted);
      const formatted = formatDashedPhone(digits);
      setForm((p) => ({ ...p, phoneNumber: formatted }));
      if (errors.phoneNumber)
        setErrors((p) => ({ ...p, phoneNumber: undefined }));
    },
    [errors.phoneNumber]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target as {
        name: keyof MarketingForm;
        value: string;
      };
      if (name === "phoneNumber") {
        const formatted = formatDashedPhone(onlyDigits(value));
        setForm((p) => ({ ...p, phoneNumber: formatted }));
        if (errors.phoneNumber)
          setErrors((p) => ({ ...p, phoneNumber: undefined }));
        return;
      } else if (name === "websiteUrl") {
        const trimmed = value.trim();

        // always store what user typed (no preformatting)
        setForm((p) => ({ ...p, websiteUrl: value }));

        // if field is optional, clear error when empty
        if (!trimmed) {
          if (errors.websiteUrl) {
            setErrors((p) => ({ ...p, websiteUrl: undefined }));
          }
          return;
        }

        const isValid = validateWebsite(trimmed);

        setErrors((p) => ({
          ...p,
          websiteUrl: isValid ? undefined : "Enter a valid website URL",
        }));

        return;
      }
      setForm((p) => ({ ...p, [name]: value }));
      if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
    },
    [errors]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const toggleOption = (opt: MarketingCommentType) => {
    setForm((p) => {
      const exists = p.marketingCommentType.includes(opt);
      const updated = exists
        ? p.marketingCommentType.filter((x) => x !== opt)
        : [...p.marketingCommentType, opt];
      if (errors.marketingCommentType)
        setErrors((er) => ({ ...er, marketingCommentType: undefined }));
      return { ...p, marketingCommentType: updated };
    });
  };

  const selectedLabel = useMemo(() => {
    if (!form.marketingCommentType.length) return "";
    if (form.marketingCommentType.length <= 2)
      return form.marketingCommentType.join(", ");
    return `${form.marketingCommentType.length} selected`;
  }, [form.marketingCommentType]);

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    window.location.reload();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errs: Partial<Record<keyof MarketingForm, string>> = {};
    if (!form.companyName.trim())
      errs.companyName = "Company Name is required.";
    if (!form.phoneNumber.trim())
      errs.phoneNumber = "Phone Number is required.";
    else if (!isDashedPhoneValid(form.phoneNumber))
      errs.phoneNumber = "Use ###-###-#### format.";
    if (!form.email.trim()) errs.email = "Email is required.";
    if (!form.contactName.trim())
      errs.contactName = "Contact Name is required.";
    if (!form.marketingCommentType.length)
      errs.marketingCommentType = "Select at least one interest.";
    if (form.websiteUrl && !validateWebsite(form.websiteUrl))
      errs.websiteUrl = "Enter a valid website URL";
    if (!form.branchName.length)
      errs.branchName = "Lansing branch is required.";

    setErrors(errs);

    if (Object.keys(errs).length) return;

    try {
      setSubmitting(true);

      await ensureRecaptchaReady(siteKey);
      const captchaToken = await window.grecaptcha!.execute(siteKey!, {
        action: "marketing_contact",
      });

      const formData = new FormData();

      formData.append("CaptchaToken", captchaToken);
      formData.append("CompanyName", form.companyName.trim());
      formData.append("PhoneNumber", onlyDigits(form.phoneNumber));
      formData.append("Email", form.email.trim());
      formData.append("BranchName", form.branchName.trim());
      formData.append("ContactName", form.contactName.trim());

      if (form.branchId != null) {
        formData.append("BranchId", String(form.branchId));
      }
      if (form.comment.trim()) {
        formData.append("Comment", form.comment.trim());
      }
      if (form.websiteUrl.trim()) {
        formData.append("WebsiteUrl", form.websiteUrl.trim());
      }

      branches.forEach((b) => {
        formData.append("Branches", b);
      });

      form.marketingCommentType.forEach((m) => {
        formData.append("MarketingCommentType", m);
      });

      if (logoFile) {
        formData.append("Logo", logoFile);
      }

      const resp = await dataService.sendMarketingContactUs(formData);

      if (!resp.success) {
        throw new Error(resp.message || "Failed to send.");
      }

      setSuccessDialogOpen(true);
    } catch (err: any) {
      console.error("submit failed:", err);
      setToast({
        open: true,
        msg: err?.message || "Failed to send.",
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };
  const linkSx = {
    ...baseText,
    color: "#62b5e5",
    fontWeight: 700,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  } as const;

  return (
    <>
      <Box sx={{ mt: 0, px: 0, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#fff",
            marginBottom: "50px",
            padding: "0",
            textAlign: "left",
          }}
        >
          <img
            src={MarketingServicesHeaderImage}
            alt="Marketing Services Header"
            style={{
              display: "block",
              width: "100%",
              minHeight: "180px",
              objectFit: "cover",
              objectPosition: "left top",
            }}
          />
        </Box>
        <Grid container spacing={2} sx={{ px: 4 }}>
          <Grid item xs={12} md={5.5}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Get in touch with Lansing Marketing Services
            </Typography>
            <Typography sx={{ mt: 2 }}>
              We're here to help you elevate your visibility and grow your
              business. Complete the quick form below to connect with a Lansing
              team member and get started today.
            </Typography>

            {/* Contact Us */}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                {/* Contact Name */}
                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Contact Name{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>
                  <BorderedInput
                    name="contactName"
                    value={form.contactName}
                    onChange={handleChange}
                    required
                  />
                  {errors.contactName && (
                    <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                      {errors.contactName}
                    </Typography>
                  )}
                </Grid>

                {/* Company Name */}
                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",
                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Business Name
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>
                  <BorderedInput
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    required
                  />
                  {errors.companyName && (
                    <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                      {errors.companyName}
                    </Typography>
                  )}
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Business email address{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>
                  <BorderedInput
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    type="email"
                  />
                  {errors.email && (
                    <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                      {errors.email}
                    </Typography>
                  )}
                </Grid>
                {/* Phone Number */}
                <Grid item xs={12}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Phone Number{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>
                  <BorderedInput
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    required
                    type="tel"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "\\d{3}-\\d{3}-\\d{4}",
                      maxLength: 12,
                    }}
                    onKeyDown={handlePhoneKeyDown}
                    onPaste={handlePhonePaste}
                  />
                  {errors.phoneNumber && (
                    <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                      {errors.phoneNumber}
                    </Typography>
                  )}
                </Grid>

                {/* Website URL */}
                <Grid item xs={12}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Bussines website URL
                  </FormLabel>
                  <BorderedInput
                    name="websiteUrl"
                    value={form.websiteUrl}
                    onChange={handleChange}
                  />
                  {errors.websiteUrl && (
                    <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                      {errors.websiteUrl}
                    </Typography>
                  )}
                </Grid>

                {/* Branch */}
                <Grid item xs={12}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    Select your closest Lansing branch location{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>
                  <Box
                    sx={{
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      <Autocomplete
                        fullWidth
                        options={branches}
                        value={form.branchName || null}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        onChange={(_, newValue) => {
                          const val = (newValue as string) ?? "";
                          setForm((p) => ({
                            ...p,
                            branchName: val,
                          }));
                        }}
                        onInputChange={(_, newInputValue) => {
                          setForm((p) => ({ ...p, branchName: newInputValue }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            placeholder="Type to search branches"
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              sx: {
                                ...baseText,
                                color: "#494440",
                                py: 0.5,
                                px: 1,
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                    {errors.branchName && (
                      <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                        {errors.branchName}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {/* Multiselect marketing options */}
                <Grid item xs={12} sx={{ mb: 3 }}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      mx: "auto",
                      ...baseText,
                    }}
                  >
                    What marketing services are you interested in?{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        padding: 1,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      *
                    </Box>
                  </FormLabel>

                  <Button
                    onClick={handleOpenMenu}
                    fullWidth
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      flex: 1,
                      height: "36px",
                      border: "1.75px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "4px 10px",
                      backgroundColor: "transparent",
                      color: "#494440",
                      textTransform: "none",
                      fontWeight: 500,
                      justifyContent: "start",
                    }}
                  >
                    {selectedLabel || "Please select"}
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleCloseMenu}
                    keepMounted
                  >
                    {MARKETING_OPTIONS.map((opt) => {
                      const checked = form.marketingCommentType.includes(
                        opt.value
                      );
                      return (
                        <MenuItem
                          key={opt.value}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: "#494440",
                            ...baseText,
                          }}
                        >
                          <FormControlLabel
                            label={opt.label}
                            labelPlacement="end"
                            control={
                              <Checkbox
                                checked={checked}
                                disableRipple
                                sx={{
                                  p: 0,
                                  mr: 1,
                                  "& .MuiSvgIcon-root": { fontSize: 16 },
                                }}
                                onChange={() => toggleOption(opt.value)}
                              />
                            }
                            sx={{
                              m: 0,
                              alignItems: "center",
                              ".MuiFormControlLabel-label": {
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Menu>

                  {errors.marketingCommentType && (
                    <Typography
                      sx={{ ...errorTextSx, mt: 1, width: "80%", mx: "auto" }}
                    >
                      {errors.marketingCommentType}
                    </Typography>
                  )}
                </Grid>
                {/* Logo upload */}
                <Grid item xs={12}>
                  <LogoUpload onFileChange={setLogoFile} />
                </Grid>

                {/* Comment */}
                <Grid item xs={12}>
                  <FormLabel
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      mb: 1,
                      color: "#494440",

                      ...baseText,
                    }}
                  >
                    Comment
                  </FormLabel>

                  <Box
                    sx={{
                      border: "1px solid #d9d9d9",
                      backgroundColor: "transparent",
                    }}
                  >
                    <TextField
                      variant="standard"
                      fullWidth
                      multiline
                      minRows={10}
                      value={form.comment}
                      name="comment"
                      onChange={handleChange}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          ...baseText,
                          color: "#666",
                          resize: "none",
                          p: 1,
                          backgroundColor: "transparent",
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Box
                    className="buttons"
                    sx={{ textAlign: "center", mt: 2, pb: "15px" }}
                  >
                    <Button
                      type="submit"
                      className="button-1"
                      sx={{
                        height: "50px",
                        border: "1.75px solid #54afe2",
                        borderRadius: "4px",
                        padding: "0 35px",
                        backgroundColor: "transparent",
                        color: "#62b5e5",
                        textTransform: "none",
                        fontWeight: 600,
                        ...baseText,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#54afe2",
                          color: "#fff",
                        },
                      }}
                    >
                      {submitting ? "Sending..." : "SUBMIT"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={0.5}></Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: "bold", mb: 3 }}>
              Need something else?
            </Typography>
            <Typography>
              Find answers to common questions in our{" "}
              <Link href="/faq" sx={linkSx}>
                FAQ
              </Link>
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box>
              {form.branchUrl && (
                <>
                  <Box>
                    <Typography>
                      For product and order inquiries, contact your{" "}
                      <Link
                        style={{ fontWeight: "bold" }}
                        href={
                          "/" +
                          (isAuthenticated
                            ? process.env.REACT_APP_STOREFRONT_SUBDIRECTORY
                            : process.env.REACT_APP_SUBDIRECTORY) +
                          form.branchUrl
                        }
                        sx={linkSx}
                      >
                        local branch
                      </Link>
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Typography>
                For LansingNow support, email{" "}
                <span style={{ fontWeight: "bold" }}>
                  helpnow@lansingbp.com
                </span>{" "}
                or call <span style={{ fontWeight: "bold" }}>888-631-0085</span>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          mt: 4,
          mx: "auto",
          px: 2,
        }}
      >
        {/* Error Snackbar */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={toast.sev}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            variant="filled"
            sx={{ ...baseText }}
          >
            {toast.msg}
          </Alert>
        </Snackbar>

        {/* Success Dialog */}
        <Dialog
          open={successDialogOpen}
          onClose={handleSuccessClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent
            sx={{
              textAlign: "center",
              py: 4,
              ...baseText,
            }}
          >
            <Typography
              sx={{
                ...baseText,
                fontSize: "16px",
                fontWeight: 500,
                mb: 3,
              }}
            >
              Thanks - a Lansing Marketing Team member will reach out to you in
              the next 48 hours.
            </Typography>
            <DialogActions sx={{ justifyContent: "center", p: 0 }}>
              <Button
                variant="contained"
                onClick={handleSuccessClose}
                sx={{
                  textTransform: "none",
                  px: 4,
                  ...baseText,
                  backgroundColor: "#54afe2",
                  "&:hover": {
                    backgroundColor: "#3c96c8",
                  },
                }}
              >
                Continue
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
}
